import React from 'react';
import { Link } from 'react-router-dom';
import './Terms.css';

const Terms = () => {
  return (
    <div className="terms-page">
      <div className="terms-container">
        <Link to="/play" className="back-link">
          ← Back to Game
        </Link>

        <h1 className="terms-title">Terms of Service</h1>
        <p className="terms-updated">Last Updated: November 6, 2025</p>

        <div className="terms-content">
          <section className="terms-section">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing or using Carnivores Adapting Terribly LLC ("the Game", "the Service",
              "we", "us", or "our") at carnivoresadaptingterribly.com, you agree to be bound by
              these Terms of Service ("Terms"). If you do not agree to these Terms, do not use the
              Service.
            </p>
            {/* TODO: After LLC formation, add business address here */}
          </section>

          <section className="terms-section">
            <h2>2. Age Requirement</h2>
            <p className="highlight-text">
              <strong>You must be at least 18 years old to use this Service.</strong> The Game
              contains mature content, adult humor, and language that is not suitable for minors. By
              using the Service, you represent and warrant that you are at least 18 years of age.
            </p>
          </section>

          <section className="terms-section">
            <h2>3. Account and Game Access</h2>

            <h3>3.1 User Accounts</h3>
            <ul>
              <li>
                You may play as a guest (temporary account) or authenticate with Google/Discord
                OAuth (persistent account)
              </li>
              <li>
                <strong>OAuth Users:</strong> Your account is linked to your Google or Discord
                identity
              </li>
              <li>
                <strong>Guest Users:</strong> Your account is temporary and lost when your browser
                session ends
              </li>
              <li>You are responsible for maintaining the confidentiality of your account</li>
              <li>You are responsible for all activities that occur under your account</li>
              <li>You agree to provide accurate information when creating your account</li>
            </ul>

            <h3>3.2 Room Codes</h3>
            <ul>
              <li>Room codes are temporary and for single-session use only</li>
              <li>Sharing room codes is at your own discretion</li>
              <li>We are not responsible for unwanted players joining via shared codes</li>
            </ul>

            <h3>3.3 JWT Tokens and Session Management</h3>
            <p>
              When you authenticate with Google or Discord, we issue JWT (JSON Web Token) tokens to
              maintain your session:
            </p>
            <ul>
              <li>
                <strong>Token Storage:</strong> JWT tokens are stored in your browser's localStorage
              </li>
              <li>
                <strong>Token Expiration:</strong> Tokens expire after 7 days (configurable) and
                must be refreshed
              </li>
              <li>
                <strong>Token Security:</strong> You are responsible for keeping your device secure.
                Anyone with access to your device can access your account
              </li>
              <li>
                <strong>Account Deletion:</strong> You can delete your account and all associated
                data at any time via the Settings page
              </li>
              <li>
                <strong>Data Retention:</strong> OAuth user data is automatically deleted after 90
                days of inactivity
              </li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>4. Acceptable Use Policy</h2>
            <p>You agree NOT to:</p>
            <ul>
              <li>Use the Service if you are under 18 years of age</li>
              <li>Harass, abuse, or harm other players</li>
              <li>Use the Service for any illegal purposes</li>
              <li>
                <strong>Create write-in cards containing credible threats</strong>
              </li>
              <li>
                <strong>
                  Use write-in cards to share personally identifiable information of others
                </strong>
              </li>
              <li>
                <strong>Create write-in cards with content that violates applicable laws</strong>
              </li>
              <li>Attempt to hack, reverse engineer, or compromise the Service</li>
              <li>Create multiple accounts to circumvent bans or restrictions</li>
              <li>Use automated systems or bots (except our provided bot players)</li>
              <li>Impersonate others or misrepresent your identity</li>
              <li>Share content that violates any applicable laws</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>5. Game Content</h2>

            <h3>5.1 Official Content</h3>
            <ul>
              <li>All game cards and content are intended as parody and humor</li>
              <li>Content may be offensive, adult-oriented, or politically incorrect</li>
              <li>By playing, you acknowledge that you may encounter content you find offensive</li>
              <li>We do not endorse the views expressed in game content</li>
            </ul>

            <h3>5.2 User Behavior</h3>
            <ul>
              <li>Players are responsible for their card selections and combinations</li>
              <li>Creating offensive combinations targeting specific individuals is prohibited</li>
              <li>We reserve the right to remove players who violate community standards</li>
            </ul>

            <h3>5.3 Write-In Cards and User-Generated Content</h3>
            <p className="highlight-text">
              <strong>Invite-Only Rooms:</strong> All game rooms are invite-only - we do not offer
              public matchmaking. You control who joins your game by sharing the room code.
            </p>
            <p>
              <strong>Write-In Cards and Custom Decks:</strong> Players may create custom answer
              cards during gameplay or upload custom card decks. You are solely responsible for this
              content.
            </p>
            <p>
              <strong>Prohibited Content:</strong> Write-in cards and custom decks must NOT contain:
            </p>
            <ul>
              <li>Credible threats of violence against specific individuals or institutions</li>
              <li>
                Personally identifiable information (names, addresses, phone numbers) of real people
                without consent
              </li>
              <li>Content that violates local, state, or federal law</li>
              <li>Instructions for illegal activities</li>
            </ul>
            <p>
              <strong>Enforcement:</strong> We do not actively moderate write-in content. Room hosts
              can remove disruptive players from their games. We reserve the right to disable
              features or ban users at our discretion, and may cooperate with law enforcement if
              legally required.
            </p>
          </section>

          <section className="terms-section">
            <h2>6. Intellectual Property</h2>

            <h3>6.1 Game Content</h3>
            <ul>
              <li>
                The Service and its original content, features, and functionality are owned by
                Carnivores Adapting Terribly LLC
              </li>
              <li>
                The game concept is based on Cards Against Humanity®, which is distributed under a
                Creative Commons BY-NC-SA 4.0 license
              </li>
              <li>Our implementation, code, and unique features are proprietary</li>
            </ul>

            <h3>6.2 User Content</h3>
            <ul>
              <li>Any content you create during gameplay remains yours</li>
              <li>
                By using the Service, you grant us a license to display your username and game
                statistics
              </li>
              <li>We do not store or retain custom content between sessions</li>
            </ul>

            <h3>6.3 Content Verification & Card Attribution</h3>
            <p className="legal-text">
              All cards in Carnivores Adapting Terribly LLC are assigned a unique card ID to
              distinguish between different content sources:
            </p>
            <ul>
              <li>
                <strong>Official Cards</strong> (format: <code>black_123_ABC...</code> or{' '}
                <code>white_456_XYZ...</code>) - Cards created and published by Carnivores Adapting
                Terribly
              </li>
              <li>
                <strong>Write-In Cards</strong> (format:{' '}
                <code>write-in-white-1234567890-abc...</code>) - User-generated content created
                during gameplay
              </li>
              <li>
                <strong>Custom Deck Cards</strong> (format:{' '}
                <code>custom_white_deck_1234567890_5</code>) - Cards uploaded by users from
                third-party decks
              </li>
            </ul>
            <p className="legal-text">
              Official cards can be verified in our{' '}
              <Link to="/official-cards" className="inline-link">
                Official Card Directory
              </Link>
              , which lists all cards published by Carnivores Adapting Terribly LLC with their
              unique IDs and text. User-generated content (write-ins and custom decks) is identified
              by card ID format starting with <code>write-in-*</code> or <code>custom-*</code>. This
              content is not stored on our servers, exists only during ephemeral game sessions, and
              is the sole responsibility of the user who created or uploaded it.
            </p>
          </section>

          <section className="terms-section">
            <h2>7. Privacy and Data Collection</h2>

            <h3>7.1 Information We Collect</h3>
            <ul>
              <li>Usernames (chosen by you)</li>
              <li>
                <strong>OAuth Data:</strong> Email, username, profile picture, and unique user ID
                from Google/Discord (if you log in)
              </li>
              <li>
                <strong>JWT Tokens:</strong> Authentication tokens stored in your browser
              </li>
              <li>
                <strong>User Activity:</strong> Last active timestamp for automated data cleanup
              </li>
              <li>Game statistics (wins, rounds played)</li>
              <li>Technical data (IP address for rate limiting)</li>
              <li>Room codes and session data (temporary)</li>
            </ul>

            <h3>7.2 Information We DON'T Collect</h3>
            <ul>
              <li>Real names or personal information</li>
              <li>Payment information (until monetization features are enabled)</li>
              <li>Chat messages or communications</li>
              <li>Long-term behavioural data</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>8. Bot Players</h2>
            <ul>
              <li>Bot players are provided for smaller groups</li>
              <li>Bots are clearly marked and distinguishable from human players</li>
              <li>Bot behavior is randomized and not representative of our views</li>
              <li>Maximum of 2 bots per game room</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>9. Disclaimer of Warranties</h2>
            <p className="legal-text">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND. WE
              SPECIFICALLY DISCLAIM:
            </p>
            <ul>
              <li>Uninterrupted or error-free service</li>
              <li>Accuracy or reliability of game content</li>
              <li>Accuracy, legality, or appropriateness of user-generated write-in cards</li>
              <li>Responsibility for content created by other players</li>
              <li>Fitness for a particular purpose</li>
              <li>Non-infringement of third-party rights</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>10. Limitation of Liability</h2>
            <p className="legal-text">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR:
            </p>
            <ul>
              <li>Indirect, incidental, or consequential damages</li>
              <li>Loss of profits or data</li>
              <li>Emotional distress from game content</li>
              <li>
                Offensive, illegal, or threatening content created by other players in write-in
                cards
              </li>
              <li>Actions taken by law enforcement based on write-in card content</li>
              <li>Consequences resulting from your creation of inappropriate write-in content</li>
              <li>Actions of other players</li>
              <li>Technical issues or service interruptions</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>11. Indemnification</h2>
            <p>
              You agree to indemnify and hold harmless Carnivores Adapting Terribly LLC from any
              claims arising from:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any rights of another party</li>
              <li>Content you create or share while using the Service</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>12. Termination</h2>
            <p>We reserve the right to:</p>
            <ul>
              <li>Terminate or suspend your access immediately, without prior notice</li>
              <li>Remove or disable access to any content at our sole discretion</li>
              <li>Ban IP addresses or devices that violate these Terms</li>
              <li>Modify or discontinue the Service at any time</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>13. Legal Compliance and Data Availability</h2>

            <h3>What Data We Have</h3>
            <ul>
              <li>Write-in cards are deleted immediately when games end</li>
              <li>We do not retain historical write-in content</li>
              <li>
                For active sessions, we may have: IP addresses, timestamps, room codes, current
                write-in text
              </li>
            </ul>

            <h3>Law Enforcement Requests</h3>
            <ul>
              <li>We will comply with valid legal requests to the extent possible</li>
              <li>Our cooperation is limited to data we actively possess at the time of request</li>
              <li>We cannot recover write-in content from ended sessions</li>
            </ul>

            <h3>No Active Moderation</h3>
            <ul>
              <li>We do not monitor or pre-screen write-in content</li>
              <li>We do not have a reporting system</li>
              <li>Room hosts are responsible for managing their own games</li>
            </ul>

            <h3>Your Responsibility</h3>
            <p>By using write-in features, you acknowledge and agree that:</p>
            <ul>
              <li>You are solely responsible for your write-in content</li>
              <li>We may provide available information to authorities if legally required</li>
              <li>Invite-only design means you control who joins your game</li>
            </ul>
          </section>

          <section className="terms-section">
            <h2>14. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. Changes will be effective
              immediately upon posting. Your continued use of the Service constitutes acceptance of
              modified Terms.
            </p>
          </section>

          <section className="terms-section">
            <h2>15. Parody and Fair Use Notice</h2>
            <p>
              This game is a parody work intended for entertainment purposes only. Cards Against
              Humanity® is a trademark of Cards Against Humanity LLC. We are not affiliated with,
              endorsed by, or sponsored by Cards Against Humanity LLC. Our use constitutes fair use
              and parody under applicable law.
            </p>
          </section>

          <section className="terms-section">
            <h2>16. Contact Information</h2>
            <p>
              For questions about these Terms, <Link to="/contact">use our contact form</Link>.
            </p>
          </section>

          <section className="terms-section">
            <h2>17. Severability</h2>
            <p>
              If any provision of these Terms is found to be unenforceable, the remaining provisions
              will continue in full force and effect.
            </p>
          </section>

          <section className="terms-section">
            <h2>18. Entire Agreement</h2>
            <p>
              These Terms constitute the entire agreement between you and Carnivores Adapting
              Terribly regarding the use of the Service.
            </p>
          </section>
        </div>

        <div className="terms-footer">
          <p>
            <strong>
              By using the Service, you acknowledge that you have read, understood, and agree to be
              bound by these Terms of Service.
            </strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Terms;
